import React, { Children, useEffect, useState } from 'react';
import styled, { x } from '@xstyled/styled-components';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { TABS } from '../itemIds';
import { IconButton } from '~/training/sequence/templates/items/carousel/components/IconButton';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import audioEffect from '~/assets/mp3/card_flip.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { t } from 'ttag';

interface Props {
  value?: string;
  tabsTitles?: string[];
}

export type TabsDefinition = ItemDefinition<typeof TABS, Props>;

// TODO #F3ECFF move to var
const TabsNav = styled.ul`
  width: 80%;
  margin-bottom: -15px;
  z-index: 1;
  & > li {
    float: left;
    padding: 20px;
    padding-top: 10px;
    cursor: pointer;
    min-width: 200px;
    background: #f3ecff;
    box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.05),
      0px 30px 30px rgba(0, 0, 0, 0.03), 0px 15px 20px rgba(0, 0, 0, 0.05);
    border-radius: 20px;
    font-family: Recoleta;
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 150%;
    text-transform: capitalize;
    color: #000000;
  }
  .active {
    background: ${p => p.theme.colors.white};
    color: ${p => p.theme.colors.black};
  }

  @media (max-width: md) {
    display: flex;
    & > li {
      min-width: 100px;
      width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 13px;
    }
  }

  @media (max-width: sm) {
    display: none;
  }
`;

const TabsBox = styled.div`
  background-color: white;
  width: 80%;
  height: 75vh;
  overflow: hidden;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 20px;
  z-index: 2;

  & > .content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    align-content: center;
  }
  .tabContainer {
    height: 100%;
    border-top-right-radius: 20px;
  }
  .tabContent {
    display: contents;
    > div:first-child {
      padding: 16px;
      overflow: auto;
    }
    div[id^='img_'] {
      min-height: 100px;
    }
  }
`;

export class Tabs extends Item<typeof TABS, Props> {
  protected render({ children, tabsTitles }: RenderProps<Props>) {
    const [activeTab, setActiveTab] = useState(0);
    const childArray = Children.toArray(children);
    const { addBlocker, removeBlocker } = useSequenceStore();
    const variant = 'horizontal';
    const index = activeTab;
    const [playAudioEffect] = useAudioEffect(audioEffect);

    useGlobalMedia();

    const globalMedia = useGlobalMediaStore();
    const globalPlayer = globalMedia?.player;

    const prev = () => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
        setActiveTab(activeTab - 1);
      }, 0);
    };

    const next = () => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
        setActiveTab(activeTab + 1);
        removeBlocker(`tab_${activeTab + 1}`);
      }, 0);
    };

    const clickOnTab = (i: number) => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
        setActiveTab(i);
        removeBlocker(`tab_${i}`);
      }, 0);
    };

    useEffect(() => {
      for (let i = 1; i < childArray.length; i++) {
        addBlocker(`tab_${i}`);
      }
    }, []);

    return (
      <>
        <IconButton
          key="prev"
          onClick={prev}
          gridArea="prev"
          aria-label={t`Previous`}
          transform
          rotate={variant === 'horizontal' ? '180deg' : '-90deg'}
          justifySelf={variant === 'horizontal' ? 'end' : 'start'}
          alignSelf={variant === 'horizontal' ? 'center' : 'end'}
          disabled={index === 0}
          position="absolute"
          left="3%"
          tabIndex={0}
        />
        <TabsNav className="tabNav" role="tablist">
          {tabsTitles?.map((c, i) => (
            <x.li
              aria-selected={i == activeTab ? 'true' : 'false'}
              role="tab"
              key={`tabId_${i}`}
              className={i == activeTab ? 'tabBtn active' : 'tabBtn'}
              onClick={() => clickOnTab(i)}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  clickOnTab(i);
                }
              }}
              tabIndex={0}
              dangerouslySetInnerHTML={{ __html: c }}
            ></x.li>
          ))}
        </TabsNav>
        <TabsBox>
          <x.div className="tabContainer">
            {childArray.map((c, i) => {
              if (activeTab != i) return null;

              return (
                <x.div key={`tabContent_${i}`} className="tabContent">
                  {c}
                </x.div>
              );
            })}
          </x.div>
        </TabsBox>
        <IconButton
          key="next"
          onClick={next}
          gridArea="next"
          aria-label={t`Next`}
          transform
          rotate={variant === 'horizontal' ? '0deg' : '90deg'}
          justifySelf="start"
          alignSelf={variant === 'horizontal' ? 'center' : 'start'}
          disabled={index === childArray.length - 1}
          position="absolute"
          right="3%"
          tabIndex={0}
        />
      </>
    );
  }
}

registry[TABS] = Tabs;
